import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Home, FileText, DollarSign, CreditCard, Wrench, Upload, AlertCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantPortal = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedAccrual, setSelectedAccrual] = useState(null);
  const [requestForm, setRequestForm] = useState({
    category: '',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTenantData();
  }, []);

  const fetchTenantData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenant-portal/my-info`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching tenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!data?.contract?.unit_id) {
      toast.error('No active contract found');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/maintenance-requests`, {
        unit_id: data.contract.unit_id,
        ...requestForm
      }, { withCredentials: true });
      toast.success(t('saveSuccess'));
      setIsRequestModalOpen(false);
      setRequestForm({ category: '', description: '', priority: 'medium' });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleReceiptUpload = async (e) => {
    e.preventDefault();
    const file = e.target.receipt?.files[0];
    if (!file || !selectedAccrual) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/api/tenant-portal/upload-receipt?accrual_id=${selectedAccrual.id}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success('Receipt uploaded successfully');
      setIsUploadModalOpen(false);
      setSelectedAccrual(null);
      fetchTenantData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (!data?.tenant) {
    return (
      <div className="text-center py-12" data-testid="tenant-portal-no-data">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">No Tenant Record Found</h2>
        <p className="text-gray-500">Please contact your property manager to link your account.</p>
      </div>
    );
  }

  const totalDebt = data.accruals?.reduce((sum, a) => sum + a.remaining_amount, 0) || 0;
  const unpaidAccruals = data.accruals?.filter(a => a.remaining_amount > 0) || [];

  return (
    <div className="space-y-8" data-testid="tenant-portal">
      {/* Welcome Header */}
      <div className="border-b border-gray-200 pb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter">
          {t('tenantPortal')}
        </h1>
        <p className="text-gray-500 mt-1">
          Welcome, {data.tenant.name}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Unit Info */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="portal-unit-info">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('units')}
              </p>
              <p className="font-heading text-2xl font-bold">
                {data.unit?.name || 'No Unit'}
              </p>
            </div>
            <div className="p-2 bg-gray-100">
              <Home className="w-5 h-5" />
            </div>
          </div>
          {data.contract && (
            <p className="mt-2 text-sm text-gray-500">
              Contract ends: {data.contract.end_date?.split('T')[0]}
            </p>
          )}
        </div>

        {/* Total Debt */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="portal-debt">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('outstandingDebt')}
              </p>
              <p className={`font-heading text-2xl font-bold ${totalDebt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                ${totalDebt.toFixed(2)}
              </p>
            </div>
            <div className={`p-2 ${totalDebt > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
              <DollarSign className={`w-5 h-5 ${totalDebt > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {unpaidAccruals.length} unpaid items
          </p>
        </div>

        {/* Monthly Rent */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="portal-rent">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('monthlyRent')}
              </p>
              <p className="font-heading text-2xl font-bold">
                ${data.contract?.monthly_rent || 0}
              </p>
            </div>
            <div className="p-2 bg-gray-100">
              <FileText className="w-5 h-5" />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Due on day {data.contract?.due_day || 1} of each month
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pending Payments */}
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold">{t('myDebts')}</h3>
          </div>
          <div className="space-y-3">
            {unpaidAccruals.length > 0 ? (
              unpaidAccruals.map((accrual) => (
                <div key={accrual.id} className="flex items-center justify-between p-3 border border-gray-100 hover:border-black transition-colors">
                  <div>
                    <p className="font-medium text-sm capitalize">{accrual.accrual_type}</p>
                    <p className="text-xs text-gray-500">Due: {accrual.due_date?.split('T')[0]}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-red-600 font-bold">${accrual.remaining_amount}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none text-xs"
                      onClick={() => { setSelectedAccrual(accrual); setIsUploadModalOpen(true); }}
                      data-testid={`upload-receipt-${accrual.id}`}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      {t('uploadReceipt')}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center py-8 text-gray-400">{t('noData')}</p>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="font-heading text-lg font-bold mb-4">{t('myPayments')}</h3>
          <div className="space-y-3">
            {data.payments?.length > 0 ? (
              data.payments.slice(0, 5).map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 border border-gray-100">
                  <div>
                    <p className="font-medium text-sm">{payment.payment_date?.split('T')[0]}</p>
                    <p className="text-xs text-gray-500 capitalize">{payment.payment_method}</p>
                  </div>
                  <span className="font-mono text-emerald-600 font-bold">+${payment.amount}</span>
                </div>
              ))
            ) : (
              <p className="text-center py-8 text-gray-400">{t('noData')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Bank Accounts for Payment */}
      {data.bank_accounts?.length > 0 && (
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="font-heading text-lg font-bold mb-4">{t('paymentInformation')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.bank_accounts.map((account) => (
              <div key={account.id} className="p-4 bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="font-semibold">{account.bank_name}</span>
                </div>
                <p className="text-sm text-gray-600">{account.account_holder}</p>
                <p className="font-mono text-sm mt-1 break-all">{account.iban}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Maintenance Request Button */}
      <div className="flex justify-end">
        <Button
          onClick={() => setIsRequestModalOpen(true)}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="submit-maintenance-request-btn"
        >
          <Wrench className="w-4 h-4 mr-2" />
          {t('submitRequest')}
        </Button>
      </div>

      {/* Maintenance Request Modal */}
      <Dialog open={isRequestModalOpen} onOpenChange={setIsRequestModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">{t('submitRequest')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('category')} *</label>
              <select
                value={requestForm.category}
                onChange={(e) => setRequestForm(prev => ({ ...prev, category: e.target.value }))}
                required
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="">Select category</option>
                <option value="plumbing">Plumbing</option>
                <option value="electrical">Electrical</option>
                <option value="hvac">HVAC</option>
                <option value="appliance">Appliance</option>
                <option value="structural">Structural</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('priority')}</label>
              <select
                value={requestForm.priority}
                onChange={(e) => setRequestForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="low">{t('low')}</option>
                <option value="medium">{t('medium')}</option>
                <option value="high">{t('high')}</option>
                <option value="urgent">{t('urgent')}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('description')} *</label>
              <textarea
                value={requestForm.description}
                onChange={(e) => setRequestForm(prev => ({ ...prev, description: e.target.value }))}
                required
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="4"
                placeholder={t('describeIssue')}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsRequestModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">{t('uploadReceipt')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReceiptUpload} className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Upload your payment receipt for: <strong>{selectedAccrual?.accrual_type}</strong> - ${selectedAccrual?.remaining_amount}
              </p>
              <Input
                type="file"
                name="receipt"
                accept="image/*,.pdf"
                required
                className="rounded-none"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsUploadModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800">
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantPortal;
